<script setup lang="ts">
import SmallModel from "@/components/Interface/Modal/ModelSizes/SmallModel.vue";
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()

const percentageUsed = computed(() => {
  if (gameStore.hoursOnlineLimit === 0) return 0
  return Math.min(100, (gameStore.hoursOnline / gameStore.hoursOnlineLimit) * 100)
})

const statusText = computed(() => {
  const pct = percentageUsed.value
  if (pct < 50) return t('modals.hoursOnline.status.plenty')
  if (pct < 80) return t('modals.hoursOnline.status.gettingClose')
  if (pct < 100) return t('modals.hoursOnline.status.approaching')
  return t('modals.hoursOnline.status.atLimit')
})

const barText = computed(() => {
  const hoursLeft = Math.max(0, gameStore.hoursOnlineLimit - gameStore.hoursOnline)
  return t('modals.hoursOnline.hoursLeftText', { hours: hoursLeft })
})

// Generate last 24 hours with usage status
const last24Hours = computed(() => {
  const now = Math.floor(Date.now() / 1000)
  const currentHourStart = Math.floor(now / 3600) * 3600
  const hours = []

  // Convert active timestamps to hour-start timestamps
  const activeHourStarts = new Set(
    gameStore.activeTimestamps.map(ts => Math.floor(ts / 3600) * 3600)
  )

  // Generate exactly 24 hours starting from current hour going back
  for (let i = 0; i < 24; i++) {
    const hourStart = currentHourStart - (i * 3600)
    const isUsed = activeHourStarts.has(hourStart)

    const date = new Date(hourStart * 1000)
    const hourDisplay = date.getHours()

    hours.unshift({
      timestamp: hourStart,
      hour: hourDisplay,
      isUsed,
      isCurrent: i === 0
    })
  }

  return hours
})
</script>

<template>
  <SmallModel :title="t('modals.hoursOnline.title')">
    <StandardCard class="mx-2" top-padding="2">
      <div v-html="t('modals.hoursOnline.description', { hours: gameStore.hoursOnlineLimit })">
      </div>
    </StandardCard>
    <StandardCard class="mx-2" top-padding="2">
      <div>
        <div class="text-sm pb-1">
          <span>{{ t('modals.hoursOnline.hoursLeft') }}</span>
        </div>

        <div class="relative">
          <SimpleProgressBar
            :percentage="percentageUsed"
            color="indigo"
            :text="barText"
            :height="16"
          />
        </div>
      </div>
    </StandardCard>
    <StandardCard class="mx-2" top-padding="2" bottom-padding="2">
      <div>
        <div class="text-sm text-center" :class="{
          'text-green-400': percentageUsed < 50,
          'text-yellow-400': percentageUsed >= 50 && percentageUsed < 80,
          'text-orange-400': percentageUsed >= 80 && percentageUsed < 90,
          'text-red-400': percentageUsed >= 90
        }">
          {{ statusText }}
        </div>
      </div>
    </StandardCard>
    <StandardCard class="mx-2" top-padding="2" bottom-padding="2">
      <div>
        <div class="text-sm font-semibold pb-2">{{ t('modals.hoursOnline.last24Hours') }}</div>
        <div class="grid grid-cols-12 gap-1">
          <div
            v-for="hourData in last24Hours"
            :key="hourData.timestamp"
            class="flex flex-col items-center"
          >
            <div class="text-xs mb-1">{{ hourData.hour }}</div>
            <div
              class="w-full h-6 rounded"
              :class="{
                'bg-green-500': hourData.isUsed,
                'bg-metal-700': !hourData.isUsed
              }"
              :title="hourData.isUsed ? t('modals.hoursOnline.used') : t('modals.hoursOnline.notUsed')"
            ></div>
          </div>
        </div>
      </div>
    </StandardCard>
  </SmallModel>
</template>